// MA-271 — allowlist-outage hold guard. Mirrors onboardingHoldGuard.js's
// asymmetric-wrong-guess doctrine (only HOLD on POSITIVE proof), but for a
// different failure class: the private-beta allowlist check itself failing,
// not the students-fetch.
//
// A returning parent's account was PREVIOUSLY confirmed allowlisted by a
// clean, error-free check. If a LATER check fails outright (network/RLS/
// timeout), that positive-proof signal — not the absence of one — is what
// tells the gate this is an outage, not a real rejection: hold them at a
// recoverable retry surface instead of the honest-but-wrong "request
// access" screen. A user who has NEVER been confirmed (never-approved
// stranger, or a brand-new signup) has no such signal, so the SAME outage
// still falls through to the existing fail-closed PrivateBetaScreen — the
// wrong-guess failure mode here is "a revoked/never-approved user briefly
// sees 'try again' during a coincidental real outage," never "a real block
// is bypassed" (the gate itself never flips to allowed:true on outage — see
// useAllowlist.js, `allowed` stays false in both branches).
//
// Client-writable residue: this flag lives in localStorage, so unlike
// onboardingHoldGuard.js's server-issued `created_at`, it is not a
// server-trusted signal — a user could set it via devtools. That's an
// accepted, low-severity difference: spoofing it only ever swaps which
// COPY a user sees (retry vs. request-access) during a real outage; it
// can never grant `allowed:true` itself.
//
// localStorage (not sessionStorage): an outage can span days, and this is
// meant to survive across sessions on the same device. Keyed per Supabase
// user id so co-located accounts on a shared/kiosk device never bleed into
// each other. Storage is injectable (mirrors
// src/lib/teacher/pendingJoinCode.ts's convention) for unit testing without
// a DOM.

export const ALLOWLIST_CONFIRMED_KEY_PREFIX = "pw_allowlist_confirmed:";

function safeStorage(storage) {
  if (storage) return storage;
  return typeof localStorage === "undefined" ? undefined : localStorage;
}

/**
 * Records the outcome of a CLEAN (error-free) allowlist check so a later
 * outage can tell "previously approved" apart from "never approved."
 * Call only when checkAllowlist returned no error — sets the flag on
 * allowed:true, and CLEARS it on allowed:false. Clearing on a clean
 * rejection matters: a revoked or never-listed account must not carry a
 * stale "was confirmed" signal into a later, unrelated outage window.
 *
 * @param {string|undefined|null} userId
 * @param {boolean} allowed
 * @param {Storage} [storage]
 */
export function recordAllowlistCheckResult(userId, allowed, storage) {
  const s = safeStorage(storage);
  if (!s || !userId) return;
  const key = `${ALLOWLIST_CONFIRMED_KEY_PREFIX}${userId}`;
  try {
    if (allowed) {
      s.setItem(key, "1");
    } else {
      s.removeItem(key);
    }
  } catch {
    /* private mode / quota — best-effort only, never required */
  }
}

/**
 * @param {string|undefined|null} userId
 * @param {Storage} [storage]
 * @returns {boolean}
 */
export function wasAllowlistConfirmed(userId, storage) {
  const s = safeStorage(storage);
  if (!s || !userId) return false;
  try {
    return s.getItem(`${ALLOWLIST_CONFIRMED_KEY_PREFIX}${userId}`) === "1";
  } catch {
    return false;
  }
}

/**
 * The gate decision: HOLD at a recoverable retry surface (instead of the
 * honest PrivateBetaScreen) iff the check errored AND this user was
 * previously confirmed allowlisted. No error → never hold (moot — the
 * caller only consults this on a failed check). Error + never confirmed →
 * never hold (existing fail-closed behavior, unchanged).
 *
 * @param {object} args
 * @param {unknown} args.error - the checkAllowlist result's error (falsy
 *   when the check succeeded).
 * @param {string|undefined|null} args.userId
 * @param {Storage} [args.storage]
 * @returns {boolean}
 */
export function shouldHoldForAllowlistOutage({ error, userId, storage }) {
  if (!error) return false;
  return wasAllowlistConfirmed(userId, storage);
}
